/**
 * 音频分析和DTW对齐工具
 */

/**
 * 从AudioBuffer提取音频特征（使用能量和频谱特征）
 */
export function extractAudioFeatures(audioBuffer: AudioBuffer, hopSize: number = 2048): Float32Array[] {
  const channelData = audioBuffer.getChannelData(0) // 使用第一个声道
  const features: Float32Array[] = []
  
  // 使用滑动窗口提取特征
  const windowSize = hopSize * 2
  for (let i = 0; i < channelData.length - windowSize; i += hopSize) {
    const window = channelData.slice(i, i + windowSize)
    const feature = computeFeatureVector(window)
    features.push(feature)
  }
  
  return features
}

/**
 * 计算特征向量（RMS能量 + 频谱质心 + 频谱滚降）
 */
function computeFeatureVector(window: Float32Array): Float32Array {
  const features = new Float32Array(3)
  
  // 1. RMS能量
  let sumSquares = 0
  for (let i = 0; i < window.length; i++) {
    sumSquares += window[i] * window[i]
  }
  features[0] = Math.sqrt(sumSquares / window.length)
  
  // 2. 零交叉率 (Zero Crossing Rate)
  let zeroCrossings = 0
  for (let i = 1; i < window.length; i++) {
    if ((window[i] >= 0 && window[i - 1] < 0) || (window[i] < 0 && window[i - 1] >= 0)) {
      zeroCrossings++
    }
  }
  features[1] = zeroCrossings / window.length
  
  // 3. 简单的频谱特征（高频能量比例）
  const midPoint = Math.floor(window.length / 2)
  let lowEnergy = 0
  let highEnergy = 0
  for (let i = 0; i < midPoint; i++) {
    lowEnergy += window[i] * window[i]
  }
  for (let i = midPoint; i < window.length; i++) {
    highEnergy += window[i] * window[i]
  }
  features[2] = highEnergy / (lowEnergy + highEnergy + 1e-10)
  
  return features
}

/**
 * 计算两个特征向量之间的欧氏距离
 */
function euclideanDistance(a: Float32Array, b: Float32Array): number {
  let sum = 0
  for (let i = 0; i < a.length; i++) {
    const diff = a[i] - b[i]
    sum += diff * diff
  }
  return Math.sqrt(sum)
}

/**
 * DTW算法结果
 */
export interface DTWResult {
  distance: number // 总距离
  normalizedDistance: number // 归一化距离（相似度）
  path: Array<[number, number]> // 对齐路径
  similarity: number // 相似度百分比 (0-100)
  matchStart?: number // 匹配起始位置（在参考序列中的帧索引）
  matchEnd?: number // 匹配结束位置（在参考序列中的帧索引）
  matchStartTime?: number // 匹配起始时间（秒）
  matchEndTime?: number // 匹配结束时间（秒）
}

/**
 * Subsequence DTW算法
 * 在参考序列(features2)中查找查询序列(features1)的最佳匹配
 * features1: 查询序列（通常是实验音频，较短）
 * features2: 参考序列（通常是测试音频，较长）
 */
export function computeSubsequenceDTW(
  features1: Float32Array[],
  features2: Float32Array[],
  windowSize?: number
): DTWResult {
  const n = features1.length // 查询序列长度
  const m = features2.length // 参考序列长度
  
  console.log(`Subsequence DTW: Query length=${n}, Reference length=${m}`)
  
  // 如果查询序列比参考序列长，可能参数传反了
  if (n > m) {
    console.warn('Warning: Query sequence is longer than reference sequence')
  }
  
  // 动态调整窗口大小：基于查询序列长度，允许一定的时间伸缩
  const constraint = windowSize || Math.floor(n * 1.5)
  
  // 初始化DTW矩阵
  const dtw: number[][] = Array(n + 1).fill(0).map(() => Array(m + 1).fill(Infinity))
  
  // Subsequence DTW: 第一行初始化为0，允许在参考序列的任意位置开始匹配
  for (let j = 0; j <= m; j++) {
    dtw[0][j] = 0
  }
  
  // 填充DTW矩阵
  for (let i = 1; i <= n; i++) {
    // 动态窗口：以当前查询位置为中心，搜索可能的参考位置
    const centerJ = Math.floor((i / n) * m)
    const startJ = Math.max(1, centerJ - constraint)
    const endJ = Math.min(m, centerJ + constraint)
    
    for (let j = startJ; j <= endJ; j++) {
      const cost = euclideanDistance(features1[i - 1], features2[j - 1])
      dtw[i][j] = cost + Math.min(
        dtw[i - 1][j],     // 删除（跳过查询序列的一帧）
        dtw[i][j - 1],     // 插入（跳过参考序列的一帧）
        dtw[i - 1][j - 1]  // 匹配
      )
    }
  }
  
  // 找到最后一行的最小值（subsequence DTW的终点）
  let minDist = Infinity
  let endJ = m
  for (let j = 1; j <= m; j++) {
    if (dtw[n][j] < minDist) {
      minDist = dtw[n][j]
      endJ = j
    }
  }
  
  console.log(`Best match ends at reference frame ${endJ} with distance ${minDist.toFixed(2)}`)
  
  // 回溯路径
  const path: Array<[number, number]> = []
  let i = n
  let j = endJ
  
  while (i > 0 && j > 0) {
    path.unshift([i - 1, j - 1])
    
    const candidates = [
      { i: i - 1, j: j, val: dtw[i - 1][j] },
      { i: i, j: j - 1, val: dtw[i][j - 1] },
      { i: i - 1, j: j - 1, val: dtw[i - 1][j - 1] }
    ]
    
    // 选择最小的
    candidates.sort((a, b) => a.val - b.val)
    i = candidates[0].i
    j = candidates[0].j
    
    // 如果到达第一行，停止（subsequence起点）
    if (i === 0) break
  }
  
  // 获取匹配的起始和结束位置
  const matchStart = path.length > 0 ? path[0][1] : 0
  const matchEnd = path.length > 0 ? path[path.length - 1][1] : 0
  
  console.log(`Match range: frames ${matchStart} to ${matchEnd} (${matchEnd - matchStart + 1} frames)`)
  
  // 计算归一化距离和相似度
  const pathLength = Math.max(path.length, 1) // 避免除以0
  const normalizedDistance = minDist / pathLength
  const maxPossibleDist = Math.sqrt(3) // 由于特征向量归一化，最大距离约为sqrt(3)
  const similarity = Math.max(0, Math.min(100, (1 - normalizedDistance / maxPossibleDist) * 100))
  
  return {
    distance: minDist,
    normalizedDistance,
    path,
    similarity,
    matchStart,
    matchEnd
  }
}

/**
 * Subsequence定位DTW：在测试音频中定位实验音频
 * audioBuffer1: 实验音频（查询序列，通常较短）
 * audioBuffer2: 测试音频（参考序列，通常较长）
 */
export function computeBidirectionalDTW(
  audioBuffer1: AudioBuffer,
  audioBuffer2: AudioBuffer,
  hopSize: number = 2048
): DTWResult {
  console.log('Extracting features from experiment audio (query)...')
  const features1 = extractAudioFeatures(audioBuffer1, hopSize)
  
  console.log('Extracting features from test audio (reference)...')
  const features2 = extractAudioFeatures(audioBuffer2, hopSize)
  
  console.log(`Features extracted: Query=${features1.length} frames, Reference=${features2.length} frames`)
  console.log(`Audio durations: Experiment=${audioBuffer1.duration.toFixed(2)}s, Test=${audioBuffer2.duration.toFixed(2)}s`)
  
  // 执行subsequence DTW查找
  console.log('Computing subsequence DTW (locating experiment audio in test audio)...')
  const result = computeSubsequenceDTW(features1, features2)
  
  // 计算匹配的时间范围
  const frameToTime = (frame: number) => (frame * hopSize) / audioBuffer2.sampleRate
  
  if (result.matchStart !== undefined && result.matchEnd !== undefined) {
    result.matchStartTime = frameToTime(result.matchStart)
    result.matchEndTime = frameToTime(result.matchEnd)
    
    console.log(`Match located: ${result.matchStartTime.toFixed(2)}s to ${result.matchEndTime.toFixed(2)}s in test audio`)
    console.log(`Match duration: ${(result.matchEndTime - result.matchStartTime).toFixed(2)}s (experiment audio: ${audioBuffer1.duration.toFixed(2)}s)`)
  }
  
  console.log(`DTW Similarity: ${result.similarity.toFixed(2)}%`)
  
  return result
}

/**
 * 获取对齐信息的时间戳
 */
export interface AlignmentSegment {
  time1: number // 音频1的时间（秒）
  time2: number // 音频2的时间（秒）
  offset: number // 时间偏移（秒）
}

export function getAlignmentSegments(
  path: Array<[number, number]>,
  duration1: number,
  duration2: number,
  sampleRate: number = 44100,
  hopSize: number = 2048
): AlignmentSegment[] {
  const segments: AlignmentSegment[] = []
  const frameToTime = (frame: number) => (frame * hopSize) / sampleRate
  
  // 采样路径点（不是所有点都显示，避免太密集）
  const step = Math.max(1, Math.floor(path.length / 20))
  
  for (let i = 0; i < path.length; i += step) {
    const [idx1, idx2] = path[i]
    const time1 = frameToTime(idx1)
    const time2 = frameToTime(idx2)
    
    segments.push({
      time1,
      time2,
      offset: time2 - time1
    })
  }
  
  return segments
}

